import { useEffect, useState } from 'react';
import { ApiBase, Get } from './RemoteAuthority';

// Who we are, our games and the people we can play with, fetched from the server and kept up to date while anything on
// the page shows them (the top bar, the front page): one fetch for all of them, every few seconds.
//   me       - { login, name, provider, switchable, form, devTools }, or null
//   games    - our games (see the server's GET /games), or null until loaded
//   users    - everyone who has signed in: [{ login, name }]
//   signIn   - not signed in, and the server has a sign-in page of its own (its "password" way)
//   error    - why the server could not be asked, or null
const Every = 8000;

let lobby = { me: null, games: null, users: [], signIn: false, error: null };
const listeners = new Set();
let timer = null;

const set = (changes) => {
    lobby = { ...lobby, ...changes };
    listeners.forEach(listener => listener(lobby));
    ShowWaiting(lobby.me ? Waiting(lobby.games).length : 0);
};

// The tab: the page's title, and how many games want something from us (see NeedsMe) as "(2) " before it and a red
// badge with the number on the icon, so a tab in the background shows it is our turn. Kept on every page with the top
// bar (it asks the server every few seconds); pages set their title with SetTitle so the count stays in front of it.
let pageTitle = 'Through the Ages';
let waiting = 0;
let plainIcon = null;
const Waiting = (games) => NeedsMe(games).filter(game => game.status?.state !== 'over');

export const SetTitle = (title) => {
    pageTitle = title;
    document.title = waiting > 0 ? `(${waiting}) ${pageTitle}` : pageTitle;
};

const ShowWaiting = (count) => {
    if (count === waiting)
    {
        return;
    }
    waiting = count;
    SetTitle(pageTitle);
    DrawIcon(count);
};

// The icon with a red circle and the count in its top-right corner (the plain icon when there is nothing)
const DrawIcon = (count) => {
    const link = document.querySelector("link[rel~='icon']");
    if (!link)
    {
        return;
    }
    plainIcon ??= link.href;
    if (count === 0)
    {
        link.href = plainIcon;
        return;
    }
    const image = new Image();
    const draw = () => {
        // (a later count may have been drawn since this one started loading)
        if (count !== waiting)
        {
            return;
        }
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = size;
        const context = canvas.getContext('2d');
        if (image.complete && image.naturalWidth > 0)
        {
            context.drawImage(image, 0, 0, size, size);
        }
        context.beginPath();
        context.arc(size - 10, 10, 10, 0, 2 * Math.PI);
        context.fillStyle = '#d93025';
        context.fill();
        context.fillStyle = 'white';
        context.font = 'bold 15px Arial, sans-serif';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(count > 9 ? '9+' : String(count), size - 10, 11);
        link.href = canvas.toDataURL('image/png');
    };
    image.onload = draw;
    image.onerror = draw;
    image.src = plainIcon;
};

// Asks the server again now (e.g. after starting a game)
export const RefreshLobby = async () => {
    try {
        const [who, games, users] = await Promise.all([Get('/games/me'), Get('/games'), Get('/games/users')]);
        if (who.ok === false)
        {
            set({ me: null, games: null, users: [], signIn: !!who.signInForm, error: who.signInForm ? null : who.error });
            return;
        }
        set({ me: who, games: Array.isArray(games) ? games : [], users: Array.isArray(users) ? users : [], signIn: false, error: null });
    } catch {
        set({ error: `The game server is not answering (${ApiBase || 'this address'}).` });
    }
};

export const useLobby = () => {
    const [state, setState] = useState(lobby);
    useEffect(() => {
        listeners.add(setState);
        if (!timer)
        {
            RefreshLobby();
            timer = setInterval(RefreshLobby, Every);
        }
        return () => {
            listeners.delete(setState);
            if (listeners.size === 0)
            {
                clearInterval(timer);
                timer = null;
            }
        };
    }, []);
    return state;
};

// The games that want something from us: our turn, a decision of ours, or new (started by someone else, not yet opened)
export const NeedsMe = (games) => (games ?? []).filter(game => game.you || game.isNew);

// A game's address: as our own player; with the development tools, a game we are not in follows whoever is acting
export const GameAddress = (game) => game.mySeat >= 0 ? `./?game=${game.id}` : `./?game=${game.id}&player=follow`;

// "5 minutes ago", from the server's UTC "YYYY-MM-DD HH:MM:SS"
export const Ago = (when) => {
    if (!when) return '';
    const seconds = Math.max(0, (Date.now() - Date.parse(when.replace(' ', 'T') + 'Z')) / 1000);
    const units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
    for (const [unit, size] of units)
    {
        if (seconds >= size)
        {
            const count = Math.floor(seconds / size);
            return `${count} ${unit}${count === 1 ? '' : 's'} ago`;
        }
    }
    return 'just now';
};
